/* ═══════════════════════════════════════════════
   SPOOKER DESIGN TOKENS v3
   Strak kleur- en contrastsysteem
   ═══════════════════════════════════════════════ */

/* ─── Neutrale tekst — NOOIT lichter dan #6B7280 voor leesbare tekst ─── */
export const COLOR = {
  textPrimary:   'text-[#111827]',   // Koppen, primaire tekst
  textSecondary: 'text-[#4B5563]',   // Secundaire tekst, beschrijvingen
  textMeta:      'text-[#6B7280]',   // Metadata, datums, kleine labels
  textDisabled:  'text-[#9CA3AF]',   // Alleen uitgeschakelde content
  border:        'border-[#E5E7EB]',
  bgSoft:        'bg-[#F9FAFB]',
};

/* ─── Semantische kleuren — donkere tekstvarianten ─── */
export const SEMANTIC = {
  critical: { text: 'text-[#B91C1C]', bg: 'bg-[#FEF2F2]', border: 'border-[#FECACA]', dot: 'bg-[#B91C1C]' },
  warning:  { text: 'text-[#92400E]', bg: 'bg-[#FFFBEB]', border: 'border-[#FDE68A]', dot: 'bg-[#D97706]' },
  success:  { text: 'text-[#047857]', bg: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]', dot: 'bg-[#047857]' },
  info:     { text: 'text-[#1D4ED8]', bg: 'bg-[#EFF6FF]', border: 'border-[#BFDBFE]', dot: 'bg-[#1D4ED8]' },
  neutral:  { text: 'text-[#4B5563]', bg: 'bg-[#F3F4F6]', border: 'border-[#E5E7EB]', dot: 'bg-[#6B7280]' },
};

/* ─── Severity — donkere tekstkleuren ─── */
export const SEVERITY = {
  kritiek: { key: 'kritiek', label: 'Kritiek', color: '#B91C1C', dot: 'bg-[#B91C1C]', badge: 'bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]' },
  hoog:    { key: 'hoog',    label: 'Hoog',    color: '#92400E', dot: 'bg-[#D97706]', badge: 'bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A]' },
  midden:  { key: 'midden',  label: 'Midden',  color: '#92400E', dot: 'bg-[#D97706]', badge: 'bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A]' },
  laag:    { key: 'laag',    label: 'Laag',    color: '#1D4ED8', dot: 'bg-[#1D4ED8]', badge: 'bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]' },
  miniem:  { key: 'miniem',  label: 'Miniem',  color: '#4B5563', dot: 'bg-[#6B7280]', badge: 'bg-[#F3F4F6] text-[#4B5563] border border-[#E5E7EB]' },
};
export const getSeverity = (key) => SEVERITY[key] || SEVERITY.laag;
export const SEVERITY_LIST = Object.values(SEVERITY);

/* ─── Status — donkere tekstkleuren ─── */
export const STATUS = {
  open:        { label: 'Open',            cls: 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]' },
  mitigating:  { label: 'In behandeling',  cls: 'bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]' },
  accepted:    { label: 'Geaccepteerd',    cls: 'bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]' },
  closed:      { label: 'Gesloten',        cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' },
  planned:     { label: 'Gepland',         cls: 'bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]' },
  implemented: { label: 'Geïmplementeerd', cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' },
  verified:    { label: 'Geverifieerd',    cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' },
  draft:       { label: 'Concept',         cls: 'bg-[#F3F4F6] text-[#4B5563] border-[#E5E7EB]' },
};
export const getStatus = (key) => STATUS[key] || STATUS.open;

/* ─── Criticality ─── */
export const CRITICALITY = {
  critical: { label: 'Kritiek', cls: 'bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]' },
  high:     { label: 'Hoog',    cls: 'bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A]' },
  medium:   { label: 'Medium',  cls: 'bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A]' },
  low:      { label: 'Laag',    cls: 'bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]' },
};
export const getCriticality = (key) => CRITICALITY[key] || CRITICALITY.medium;

/* ─── Brand ─── */
export const BRAND = {
  primary: '#111827',
  accent: '#1D4ED8',
  surface: '#F9FAFB',
  card: '#FFFFFF',
  border: '#E5E7EB',
  borderLight: '#F3F4F6',
};

/* ─── Typography — donkere, leesbare tekst ─── */
export const TYPE = {
  h1:            'text-2xl font-semibold text-[#111827]',
  subtitle:      'text-sm text-[#4B5563]',
  sectionTitle:  'text-lg font-semibold text-[#111827]',

  sectionLabel:  'text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]',
  countBadge:    'text-xs font-medium text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded-full',

  kpiLabel:      'text-xs font-medium text-[#6B7280] uppercase tracking-wide',
  kpiValue:      'text-2xl font-bold',
  kpiSub:        'text-xs text-[#6B7280]',

  tableHeader:   'text-xs font-semibold uppercase tracking-wider text-[#6B7280]',
  tableCell:     'text-sm text-[#111827]',
  tableCellBold: 'text-sm font-medium text-[#111827]',
  tableCellMuted:'text-sm text-[#4B5563]',

  badgeText:     'text-xs font-semibold',
  filterChip:    'text-xs font-medium',
  mono:          'text-xs font-mono text-[#6B7280]',
  body:          'text-sm text-[#4B5563]',
  bodySmall:     'text-xs text-[#6B7280]',
};

/* ─── Spacing ─── */
export const SPACING = {
  pagePx:        'px-6',
  pagePy:        'py-4',
  pageGap:       'space-y-6',
  sectionGap:    'space-y-8',
  kpiGap:        'gap-6',
  cardPad:       'p-6',
  titleToSub:    'mt-1',
  headerToContent: 'mt-8',
  drawerW:       'w-[420px]',
  contentPx:     'px-6',
  contentPt:     'pt-6',
  contentPb:     'pb-8',
};

/* ─── Layout — twee contentmodi ─── */
export const LAYOUT = {
  dataPage:  'px-6 pt-6 pb-8',
  formPage:  'px-6 pt-6 pb-8 max-w-4xl mx-auto',
};

/* ─── Controls ─── */
export const CONTROLS = {
  primary:    'h-10 text-sm rounded-lg',
  secondary:  'h-10 text-sm rounded-lg',
  small:      'h-9 text-xs rounded-lg',
  input:      'h-10 text-sm rounded-lg',
  select:     'h-10 text-sm rounded-lg',
  search:     'h-10 text-sm rounded-lg',
};

/* ─── Table Densities ─── */
export const TABLE = {
  compact:  { header: 'h-10',  row: 'h-11',  text: 'text-xs',  pad: 'px-4 py-2' },
  standard: { header: 'h-11',  row: 'h-[52px]', text: 'text-sm', pad: 'px-4 py-3' },
  expanded: { header: 'h-12',  row: 'h-16',  text: 'text-sm',  pad: 'px-4 py-4' },
};

/* ─── Page Types ─── */
export const PAGE_TYPES = {
  list:     { layout: 'w-full' },
  centered: { layout: 'max-w-4xl mx-auto' },
};
